import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import {
  FaCheckCircle,
  FaMapMarkerAlt,
  FaClock,
  FaCalendarAlt,
  FaDollarSign,
  FaDoorOpen,
  FaCar,
  FaDog,
  FaSwimmingPool,
  FaListAlt,
} from "react-icons/fa";

/* ----------------- small helpers ----------------- */

function Section({ title, color, children }) {
  return (
    <section style={{ marginTop: "25px" }}>
      <h2 style={{ borderBottom: `2px solid ${color}`, paddingBottom: "5px" }}>
        {title}
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "15px",
          marginTop: "10px",
        }}
      >
        {children}
      </div>
    </section>
  );
}

function Info({ icon, label, value, isText = false }) {
  let displayValue = value;

  if (!isText) {
    if (
      value === true ||
      value === 1 ||
      value === "1" ||
      value === "true" ||
      value === "yes" ||
      value === "Yes"
    ) {
      displayValue = "Yes";
    } else if (
      value === false ||
      value === 0 ||
      value === "0" ||
      value === "false" ||
      value === "no" ||
      value === "No"
    ) {
      displayValue = "No";
    }
  }

  if (displayValue === null || displayValue === undefined || displayValue === "") {
    displayValue = "—";
  }

  return (
    <div
      style={{
        background: "#f8f9fa",
        borderRadius: "12px",
        padding: "14px 16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <div style={{ color: "#2e7d32", fontSize: "1.2rem" }}>
        {icon}
      </div>

      <div>
        <div style={{ fontWeight: 700 }}>{label}</div>
        <div style={{ color: "#555" }}>{displayValue}</div>
      </div>
    </div>
  );
}

function TaskRow({ label, active }) {
  return (
    <label style={taskRow}>
      <input type="checkbox" checked={!!active} readOnly />
      <span style={{ textTransform: "capitalize" }}>{label}</span>
    </label>
  );
}

/* ✅ Pool care tasks (you can change these later) */
const TASK_DEFINITIONS = [
  { key: "waterTesting", label: "water testing" },
  { key: "chemicalBalancing", label: "chemical balancing" },
  { key: "skimming", label: "skimming" },
  { key: "vacuuming", label: "vacuuming" },
  { key: "brushing", label: "brushing" },
  { key: "filterCleaning", label: "filter cleaning" },
  { key: "saltCellCleaning", label: "salt cell cleaning" },
  { key: "chemicalSupplyIncluded", label: "chemicals included" },
];

/* ----------------- main component ----------------- */

function PoolCareSwapDetails() {
  const { listingId } = useParams();
  const location = useLocation();
const fromInbox = location.state?.fromInbox === true;
  const [listing, setListing] = useState(null);
  const [message, setMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
const currentUserId = currentUser?.id;



  useEffect(() => {
    async function load() {
      try {
        setErrMsg("");
        setListing(null);

        const res = await fetch(
          `${API_BASE_URL}/api/poolcare/swap-listings/${listingId}`
        );
        const data = await res.json();

        if (!res.ok) {
          setErrMsg(data?.error || "Failed to load swap listing");
          return;
        }

        const swapListing = data.listing || data.data || data.swap || data;
        const normalisedListing = {
  ...swapListing,

  // ---- Pool details ----
  poolType:
    swapListing.poolType ??
    swapListing.pool_type ??
    null,

  poolSize:
    swapListing.poolSize ??
    swapListing.pool_size ??
    null,

  poolSurface:
    swapListing.poolSurface ??
    swapListing.pool_surface ??
    null,

  hasSpa:
    swapListing.hasSpa ??
    swapListing.has_spa ??
    false,

  // ---- Service schedule ----
  frequency:
    swapListing.frequency ??
    swapListing.service_frequency ??
    null,

  serviceDay:
    swapListing.serviceDay ??
    swapListing.service_day ??
    null,

  serviceTime:
    swapListing.serviceTime ??
    swapListing.service_time ??
    null,
};

setListing(normalisedListing);

      } catch (err) {
        console.error("Failed to load pool care swap listing:", err);
        setErrMsg("Failed to load swap listing");
      }
    }

    load();
  }, [API_BASE_URL, listingId]);

  if (errMsg) return <p style={{ padding: 20, color: "red" }}>{errMsg}</p>;
  if (!listing) return <p>Loading.</p>;

  const isOwnListing = Number(currentUserId) === Number(listing.userId);

  // --- Posted date normaliser (swap listings use mixed naming)
const formattedDate =
  listing.createdAt ||
  listing.created_at ||
  listing.postedAt ||
  listing.posted_at ||
  listing.swap_created_at
    ? new Date(
        listing.createdAt ||
          listing.created_at ||
          listing.postedAt ||
          listing.posted_at ||
          listing.swap_created_at
      ).toLocaleDateString("en-AU")
    : "—";
const getGoogleMapsLink = (suburb, postcode, state) => {
  const location = `${suburb || ""} ${postcode || ""} ${state || ""} Australia`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
};

  const services = listing.services || {};

  return (
    <div style={container}>
      {/* HERO IMAGE */}
      <img src="/images/poolcare.jpg" alt="Pool Care" style={hero} />

      <div style={{ padding: "25px" }}>
        <h2>Pool Care Swap – {listing.listingId}</h2>

<Section title="📍 Swap Locations" color="#8e24aa">
  <a
    href={getGoogleMapsLink(
      listing.swap_from_suburb,
      listing.swap_from_postcode,
      listing.swap_from_state
    )}
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: "none", color: "inherit" }}
  >
    <Info
      icon={
        <span
          style={{
            display: "inline-block",
            transition: "transform 0.2s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <FaMapMarkerAlt />
        </span>
      }
      label="From Suburb"
      value={`${listing.swap_from_suburb || "—"}, ${listing.swap_from_state || "—"} ${listing.swap_from_postcode || ""}`}
      isText
    />
  </a>

  <a
    href={getGoogleMapsLink(
      listing.swap_to_suburb,
      listing.swap_to_postcode,
      listing.swap_to_state
    )}
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: "none", color: "inherit" }}
  >
    <Info
      icon={
        <span
          style={{
            display: "inline-block",
            transition: "transform 0.2s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <FaMapMarkerAlt />
        </span>
      }
      label="To Suburb"
      value={`${listing.swap_to_suburb || "—"}, ${listing.swap_to_state || "—"} ${listing.swap_to_postcode || ""}`}
      isText
    />
  </a>
</Section>

        {/* POOL DETAILS */}
       <Section title="🏊 Pool Details" color="#0288d1">
  <Info icon={<FaSwimmingPool />} label="Pool Type" value={listing.poolType || listing.pool_type} isText />
  <Info icon={<FaSwimmingPool />} label="Pool Size" value={listing.poolSize || listing.pool_size} isText />
  <Info icon={<FaSwimmingPool />} label="Surface" value={listing.poolSurface || listing.pool_surface} isText />
  <Info icon={<FaSwimmingPool />} label="Spa Included" value={listing.hasSpa ?? listing.has_spa} />
</Section>

        {/* SERVICE SCHEDULE */}
<Section title="📅 Service Schedule" color="#43a047">
  <Info icon={<FaCalendarAlt />} label="Frequency" value={listing.frequency} isText />
  <Info icon={<FaCalendarAlt />} label="Service Day" value={listing.serviceDay || listing.service_day} isText />
  <Info icon={<FaClock />} label="Service Time" value={listing.serviceTime || listing.service_time} isText />
</Section>

        {/* INCLUDED SERVICES */}
<Section title="Included Services">
  <div style={tasksGrid}>
    {TASK_DEFINITIONS
      .filter((t) => services[t.key]) // only show selected services
      .map((t) => (
        <p key={t.key} style={{ margin: "4px 0" }}>
          ✔ {t.label}
        </p>
      ))}
  </div>
</Section>

        {/* ACCESS */}
<Section title="🚪 Access" color="#6d4c41">
  <Info icon={<FaDoorOpen />} label="Access Type" value={listing.accessType || listing.access_type} isText />
  <Info icon={<FaCar />} label="Street Parking" value={listing.streetParking} />
  <Info icon={<FaDog />} label="Pets At Home" value={listing.petsAtHome} />
  <Info icon={<FaDoorOpen />} label="Alarm Required" value={listing.alarmCodeRequired} />
</Section>

        {/* FINANCIALS */}
<Section title="💰 Financials" color="#fb8c00">
  <Info
    icon={<FaDollarSign />}
    label="Service Fee"
    value={
      listing.serviceFee !== null && listing.serviceFee !== undefined
        ? `$${Number(listing.serviceFee).toFixed(2)}`
        : "—"
    }
    isText
  />

  <Info icon={<FaListAlt />} label="Swap Status" value={listing.swap_status} isText />
</Section>

        {/* Listing Verification */}
<div style={{ marginTop: "20px" }}>
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <FaCheckCircle color="#2e7d32" />
    <strong>Posted On: {formattedDate}</strong>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "6px",
      color: "#2e7d32",
    }}
  >
    <FaCheckCircle />
    Seller confirmed this listing is genuine.
  </div>
</div>


        <div style={{ marginTop: "30px" }}>
            {/* SWAP REQUEST CTA */}
<div style={{ marginTop: "30px" }}>
{!fromInbox && !isOwnListing && (
  <>
    {message && (
      <p style={{ color: "red", fontWeight: "600", marginBottom: "10px" }}>
        {message}
      </p>
    )}

    <button
      style={swapBtn}
      onClick={() => {
        if (!currentUserId) {
          setMessage("❌ You must be logged in to request a swap.");
          setTimeout(() => (window.location.href = "/login"), 1200);
          return;
        }

        window.location.href = `/poolcare/swaps/request/${listing.listingId}`;
      }}
    >
      🔁 Swap This Pool Care Client
    </button>
  </>
)}
</div>

          <Link to="/poolcare/swaps" style={backBtn}>
            ← Back to Pool Care Swaps
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ----------------- styles ----------------- */

const container = {
  maxWidth: "900px",
  margin: "40px auto",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
  overflow: "hidden",
};

const hero = {
  width: "100%",
  height: "260px",
  objectFit: "cover",
};

const sectionTitle = {
  borderBottom: "2px solid #a020f0",
  paddingBottom: "6px",
  marginBottom: "10px",
};

const backBtn = {
  padding: "10px 16px",
  backgroundColor: "#2e7d32",
  color: "#fff",
  borderRadius: "6px",
  textDecoration: "none",
};

const tasksGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "4px 24px",
};

const taskRow = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "0.95rem",
};

const swapBtn = {
  display: "block",
  width: "100%",
  textAlign: "center",
  padding: "14px",
  backgroundColor: "#2e7d32",
  color: "#fff",
  fontSize: "1rem",
  fontWeight: "bold",
  borderRadius: "8px",
  textDecoration: "none",
  marginBottom: "15px",
};


export default PoolCareSwapDetails;

